import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';
import { t } from '@/translations';
import MasonryGallery from '@/components/MasonryGallery';
import { Button } from '@/components/ui/button';
import { motion } from 'framer-motion';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Famiglia = () => {
  const { language } = useLanguage();
  const [galleryImages, setGalleryImages] = useState([]);

  useEffect(() => {
    fetchGallery();
  }, []);

  const fetchGallery = async () => {
    try {
      const response = await axios.get(`${API}/gallery?category=family`);
      setGalleryImages(response.data);
    } catch (error) {
      console.error('Error fetching gallery:', error);
      setGalleryImages([
        {
          id: '1',
          image_url: 'https://images.unsplash.com/photo-1764747415171-4a0cb6b98dde?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwxfHxpbnRpbWF0ZSUyMGZhbWlseSUyMG1hdGVybml0eSUyMG5hdHVyYWwlMjBwaG90b2dyYXBoeXxlbnwwfHx8fDE3Nzk4MjI5ODh8MA&ixlib=rb-4.1.0&q=85',
          alt_text: 'Family maternity'
        },
        {
          id: '2',
          image_url: 'https://images.pexels.com/photos/31311613/pexels-photo-31311613.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
          alt_text: 'Family love'
        },
        {
          id: '3',
          image_url: 'https://images.unsplash.com/photo-1770261430766-ffcc3fbd8fa4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwyfHxpbnRpbWF0ZSUyMGZhbWlseSUyMG1hdGVybml0eSUyMG5hdHVyYWwlMjBwaG90b2dyYXBoeXxlbnwwfHx8fDE3Nzk4MjI5ODh8MA&ixlib=rb-4.1.0&q=85',
          alt_text: 'Couple outdoor'
        }
      ]);
    }
  };

  return (
    <div data-testid="famiglia-page" className="pt-32">
      {/* Hero Section */}
      <section className="max-w-4xl mx-auto px-6 py-16">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center"
        >
          <p className="text-xs tracking-[0.2em] uppercase text-primary font-semibold mb-4">
            {language === 'it' ? 'Famiglia & Coppia' : 'Family & Couples'}
          </p>
          <h1
            className="text-5xl sm:text-6xl font-light tracking-tight leading-tight mb-6"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
            data-testid="page-title"
          >
            {t('family.title', language)}
          </h1>
          <p className="text-lg leading-relaxed text-muted-foreground max-w-2xl mx-auto">
            {t('family.subtitle', language)}
          </p>
        </motion.div>
      </section>

      {/* Description Section */}
      <section className="max-w-6xl mx-auto px-6 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <motion.img
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            src="https://customer-assets-lqy194kg.emergentagent.net/job_remember-truly/artifacts/0g845pjp_Maternity-22.webp"
            alt="Family Photography"
            className="w-full h-auto object-cover rounded-sm"
          />
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2
              className="text-3xl sm:text-4xl font-light tracking-tight leading-tight mb-4"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              {language === 'it' ? 'Momenti Autentici' : 'Authentic Moments'}
            </h2>
            <p className="text-base leading-relaxed text-muted-foreground mb-6">
              {t('family.description', language)}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="bg-secondary/30 py-24">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-center"
            >
              <h3
                className="text-2xl sm:text-3xl font-light mb-3"
                style={{ fontFamily: 'Cormorant Garamond, serif' }}
              >
                {language === 'it' ? 'Famiglia' : 'Family'}
              </h3>
              <p className="text-sm text-muted-foreground">
                {language === 'it'
                  ? 'Sessioni rilassate per catturare il legame unico della vostra famiglia'
                  : 'Relaxed sessions to capture your family\'s unique bond'}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2, duration: 0.8 }}
              className="text-center"
            >
              <h3
                className="text-2xl sm:text-3xl font-light mb-3"
                style={{ fontFamily: 'Cormorant Garamond, serif' }}
              >
                {language === 'it' ? 'Coppia' : 'Couples'}
              </h3>
              <p className="text-sm text-muted-foreground">
                {language === 'it'
                  ? 'Raccontiamo la vostra storia d\'amore in modo autentico e naturale'
                  : 'We tell your love story in an authentic and natural way'}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4, duration: 0.8 }}
              className="text-center"
            >
              <h3
                className="text-2xl sm:text-3xl font-light mb-3"
                style={{ fontFamily: 'Cormorant Garamond, serif' }}
              >
                Maternity
              </h3>
              <p className="text-sm text-muted-foreground">
                {language === 'it'
                  ? 'Celebriamo l\'attesa con immagini delicate ed emotive'
                  : 'We celebrate the wait with delicate and emotional images'}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      {galleryImages.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 py-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mb-8"
          >
            <h2
              className="text-3xl sm:text-4xl font-light tracking-tight leading-tight text-center"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              {language === 'it' ? 'Portfolio' : 'Portfolio'}
            </h2>
          </motion.div>
          <MasonryGallery images={galleryImages} />
        </section>
      )}

      {/* CTA Section */}
      <section className="max-w-4xl mx-auto px-6 py-24 text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <h2
            className="text-4xl sm:text-5xl font-light tracking-tight leading-tight mb-8"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            {language === 'it'
              ? 'Creiamo insieme ricordi indimenticabili'
              : 'Let\'s create unforgettable memories together'}
          </h2>
          <Link to="/contatti">
            <Button size="lg" data-testid="cta-button-family">
              {t('family.cta', language)}
            </Button>
          </Link>
        </motion.div>
      </section>
    </div>
  );
};

export default Famiglia;
